/**
 * Ids — one HubSpot form drawn twice on a page (inline + the Demo pop-up, two inline copies) gets the SAME element ids
 * in both copies: HubSpot builds them from the field name and the form GUID (`email-<guid>`,
 * `requested_demo_type0-<guid>`). A `<label for>` goes to the FIRST element with its id in the document, so the second
 * copy's "Schedule Demo" ticked the first copy's radio and the second copy never submitted (2026-11-01).
 *
 * unique(form, suffix) gives every element inside one copy whose id is ALSO used outside that copy a new id
 * (`<id>--<suffix>`, the suffix being the copy's own mount id), and rewrites every reference inside the copy that
 * pointed at it (REFS). The copy drawn first keeps HubSpot's ids; the next is re-id'd. The <form> keeps its own id
 * (`hsForm_<guid>`): HubSpot and third-party tags look forms up by it, and nothing in a copy points at it.
 *
 * Idempotent: a re-id'd element no longer clashes, so a second pass changes nothing. watch() re-runs it whenever the
 * copy's nodes or ids change — HubSpot re-mounting a field (a dependent field shown, a re-render), a script adding
 * nodes — and a reference left pointing at the old id is pointed back at this copy's element (the copy remembers
 * old → new). HubSpot's own error messages carry no ids or aria references (checked 2026-11-01), so they need nothing.
 *
 * Our code never finds a field by id — always `form.querySelector('[name="…"]')`, scoped to the copy.
 */
const Ids = {};

/** Attributes that hold one id, or a space-separated list of them. */
Ids.REFS = [
  "for",
  "form",
  "list",
  "headers",
  "aria-activedescendant",
  "aria-controls",
  "aria-describedby",
  "aria-details",
  "aria-errormessage",
  "aria-flowto",
  "aria-labelledby",
  "aria-owns",
];

const REF_SELECTOR = Ids.REFS.map((name) => `[${name}]`).join(",");

/** Per copy: old id → the id it was given. */
const renamed = new WeakMap();

const inside = (form, node) => node !== form && form.contains(node);

/**
 * @param  {HTMLFormElement} form    one drawn copy
 * @param  {string}          suffix  unique to the copy (its mount's id)
 * @return {number}                  how many ids and references changed
 */
Ids.unique = function (form, suffix) {
  if (typeof form?.querySelectorAll !== "function" || !suffix) return 0;

  const doc = form.ownerDocument;

  if (typeof doc?.querySelectorAll !== "function") return 0;

  const map = renamed.get(form) || new Map();
  let changed = 0;

  // Every id in use OUTSIDE this copy (the other copies, the page).
  const taken = new Set();
  const all = [...doc.querySelectorAll("[id]")];

  all.forEach((node) => {
    if (!inside(form, node) && node.id) taken.add(node.id);
  });

  // Ids inside the copy too, so a new id never lands on one of them.
  const used = new Set(all.map((node) => node.id).filter(Boolean));

  [...form.querySelectorAll("[id]")].forEach((node) => {
    const id = node.id;

    if (!id || !taken.has(id)) return;

    let next = `${id}--${suffix}`;

    for (let n = 2; used.has(next); n++) next = `${id}--${suffix}-${n}`;

    node.id = next;
    used.add(next);
    map.set(id, next);
    changed++;
  });

  if (map.size) renamed.set(form, map);

  // References inside the copy: a token naming an id this copy renamed → the new id, when that element is still here.
  if (map.size) {
    const local = new Set(
      [...form.querySelectorAll("[id]")].map((node) => node.id).filter(Boolean),
    );

    [...form.querySelectorAll(REF_SELECTOR)].forEach((node) => {
      Ids.REFS.forEach((name) => {
        const value = node.getAttribute(name);

        if (!value) return;

        const tokens = value.split(/\s+/).filter(Boolean);
        const next = tokens.map((token) =>
          map.has(token) && !local.has(token) && local.has(map.get(token)) ? map.get(token) : token,
        );

        if (next.join(" ") !== tokens.join(" ")) {
          node.setAttribute(name, next.join(" "));
          changed++;
        }
      });
    });
  }

  return changed;
};

/**
 * unique() now, and again (once per frame at most) whenever the copy gains nodes or an id / reference changes.
 *
 * @param {HTMLFormElement} form
 * @param {string}          suffix
 */
Ids.watch = function (form, suffix) {
  Ids.unique(form, suffix);

  if (!form || form.dataset?.thryvIds || typeof window === "undefined" || !window.MutationObserver)
    return;

  form.dataset.thryvIds = suffix;

  let queued = false;
  const again = () => {
    queued = false;
    Ids.unique(form, suffix);
  };

  new window.MutationObserver(() => {
    if (queued) return;

    queued = true;
    (window.requestAnimationFrame || window.setTimeout)(again);
  }).observe(form, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["id", ...Ids.REFS],
  });
};

export default Ids;
